import type { LocaleEntry, LocaleGroup } from '../types/spark-options';
import { normalizeLocale } from './locale-rows';

/** The first use keeps the value; later repeats are the duplicates. */
function duplicateIds<T extends { id: number }>(rows: T[], keyOf: (row: T) => string): Set<number> {
  const seen = new Set<string>();
  const duplicates = new Set<number>();

  for (const row of rows) {
    const value = keyOf(row);
    if (value === '') continue;
    if (seen.has(value)) duplicates.add(row.id);
    else seen.add(value);
  }

  return duplicates;
}

export function duplicateLocaleIds(groups: LocaleGroup[]): Set<number> {
  return duplicateIds(groups, (group) => normalizeLocale(group.locale));
}

/** spark-js looks a message up by its exact key, so case is significant. */
export function duplicateEntryIds(entries: LocaleEntry[]): Set<number> {
  return duplicateIds(entries, (entry) => entry.key.trim());
}

export function hasInvalidLocaleValue(groups: LocaleGroup[] | undefined): boolean {
  if (!groups) return false;
  if (duplicateLocaleIds(groups).size > 0) return true;

  return groups.some(
    ({ locale, entries }) =>
      locale.trim() === '' ||
      duplicateEntryIds(entries).size > 0 ||
      entries.some((entry) => entry.key.trim() === ''),
  );
}
